/**
 * Typed wrapper for the StakingRewards (veTONADO) contract.
 *
 * Storage layout MUST match `staking_rewards.tolk :: StakingRewardsStorage`.
 *
 * User flow:
 *   1. Lock TONADO: send TEP-74 transfer with LockPayload (duration) as
 *      forward_payload — built via `buildLockPayload(duration)`.
 *   2. Increase amount: send TEP-74 transfer with IncreasePayload as
 *      forward_payload — built via `buildIncreasePayload()`.
 *   3. Extend duration: direct `ExtendLock` op.
 *   4. Claim rewards: direct `ClaimRewards` op.
 *   5. Withdraw: after lockEnd, direct `Withdraw` op.
 *
 * Treasury → staking reward flow:
 *   - TreasuryRouter's Distribute sends TEP-74 transfer to its own
 *     TONADO wallet with destination = stakingRewards address.
 *   - For the reward update to apply, the forward_payload should be
 *     `buildRewardPayload()`. (TreasuryRouter currently sends an
 *     empty-payload transfer, so the staking contract treats it as a
 *     reward when received from its bound wallet — see
 *     PAYLOAD_REWARD handling.)
 */
import { Address, beginCell, SendMode, toNano, } from "@ton/core";
import { JETTON_BURN_ADDRESS } from "./tonadoJetton";
export const STAKING_REWARDS_OPS = {
    PAYLOAD_LOCK: 0xded05e01,
    PAYLOAD_INCREASE: 0xded05e02,
    PAYLOAD_REWARD: 0xded05e03,
    EXTEND_LOCK: 0xded05e10,
    CLAIM_REWARDS: 0xded05e11,
    WITHDRAW: 0xded05e12,
    ADMIN_BIND_JETTON_WALLET: 0xded05e13,
    ADMIN_SET_LOCK_BOUNDS: 0xded05e14,
    ADMIN_PAUSE: 0xded057f1,
    ADMIN_UNPAUSE: 0xded057f2,
};
/** Forward_payload cell for a fresh lock. */
export function buildLockPayload(lockDurationSeconds) {
    return beginCell()
        .storeUint(STAKING_REWARDS_OPS.PAYLOAD_LOCK, 32)
        .storeUint(lockDurationSeconds, 32)
        .endCell();
}
/** Forward_payload cell for adding to an existing lock. */
export function buildIncreasePayload() {
    return beginCell()
        .storeUint(STAKING_REWARDS_OPS.PAYLOAD_INCREASE, 32)
        .endCell();
}
/** Forward_payload cell for a reward deposit. Used by TreasuryRouter or
 *  any party explicitly funding rewards. */
export function buildRewardPayload() {
    return beginCell()
        .storeUint(STAKING_REWARDS_OPS.PAYLOAD_REWARD, 32)
        .endCell();
}
export class StakingRewards {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new StakingRewards(address);
    }
    static BURN_ADDRESS = JETTON_BURN_ADDRESS;
    /**
     * MUST match `staking_rewards.tolk :: StakingRewardsStorage`.
     *
     * Layout (inline):
     *   bool       paused
     *   address    owner
     *   uint256    totalVotingPower
     *   uint256    rewardPerVoteAcc
     *   ref        accounting → StakingAccounting { undistributedRewards, walletBalance }
     *               (sub-ref'd to keep the root cell within the 1023-bit budget)
     *   Maybe ref  locks (dict<uint256, LockRecord>, null at genesis)
     *   ref        config → JettonConfig
     */
    static buildInitialData(opts) {
        const config = beginCell()
            .storeAddress(opts.tonadoMaster)
            .storeAddress(opts.stakingJettonWallet ?? JETTON_BURN_ADDRESS)
            .storeUint(opts.minLockSeconds, 32)
            .storeUint(opts.maxLockSeconds, 32)
            .endCell();
        // undistributedRewards + walletBalance are packed into a sub-ref so
        // the root cell stays within the 1023-bit budget.
        const accounting = beginCell()
            .storeCoins(opts.undistributedRewards ?? 0n)
            .storeCoins(opts.walletBalance ?? 0n)
            .endCell();
        return beginCell()
            .storeUint(opts.paused ? 1 : 0, 1)
            .storeAddress(opts.owner)
            .storeUint(0, 256) // totalVotingPower
            .storeUint(0, 256) // rewardPerVoteAcc
            .storeRef(accounting)
            .storeUint(0, 1) // locks dict = null
            .storeRef(config)
            .endCell();
    }
    static fromInit(opts) {
        const data = StakingRewards.buildInitialData(opts);
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.code)
            .storeRef(data)
            .storeUint(0, 1)
            .endCell();
        return new StakingRewards(new Address(opts.workchain ?? 0, stateInitCell.hash()), { code: opts.code, data });
    }
    // ---- Send: admin ops ----
    async sendAdminBindJettonWallet(provider, via, opts) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.ADMIN_BIND_JETTON_WALLET, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.walletAddress)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminSetLockBounds(provider, via, opts) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.ADMIN_SET_LOCK_BOUNDS, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.minLockSeconds, 32)
            .storeUint(opts.maxLockSeconds, 32)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminPause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.ADMIN_PAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminUnpause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.ADMIN_UNPAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Send: user-initiated direct ops ----
    async sendExtendLock(provider, via, opts) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.EXTEND_LOCK, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.addSeconds, 32)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendClaimRewards(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.CLAIM_REWARDS, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.2"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendWithdraw(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(STAKING_REWARDS_OPS.WITHDRAW, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.25"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Get methods ----
    async getTonadoMaster(provider) {
        const res = await provider.get("getTonadoMaster", []);
        return res.stack.readAddress();
    }
    async getStakingJettonWallet(provider) {
        const res = await provider.get("getStakingJettonWallet", []);
        return res.stack.readAddress();
    }
    async getMinLockSeconds(provider) {
        const res = await provider.get("getMinLockSeconds", []);
        return res.stack.readNumber();
    }
    async getMaxLockSeconds(provider) {
        const res = await provider.get("getMaxLockSeconds", []);
        return res.stack.readNumber();
    }
    async getTotalVotingPower(provider) {
        const res = await provider.get("getTotalVotingPower", []);
        return res.stack.readBigNumber();
    }
    async getRewardPerVoteAcc(provider) {
        const res = await provider.get("getRewardPerVoteAcc", []);
        return res.stack.readBigNumber();
    }
    async isPaused(provider) {
        const res = await provider.get("isPaused", []);
        return res.stack.readNumber() !== 0;
    }
    async isLocked(provider, owner) {
        const cell = beginCell().storeAddress(owner).endCell();
        const res = await provider.get("hasLock", [{ type: "slice", cell }]);
        return res.stack.readNumber() !== 0;
    }
    async getLock(provider, owner) {
        const cell = beginCell().storeAddress(owner).endCell();
        const res = await provider.get("getLock", [{ type: "slice", cell }]);
        return {
            amount: res.stack.readBigNumber(),
            lockEnd: res.stack.readNumber(),
            votingPower: res.stack.readBigNumber(),
            rewardDebt: res.stack.readBigNumber(),
            lastModifiedAt: res.stack.readNumber(),
        };
    }
    async getClaimable(provider, owner) {
        const cell = beginCell().storeAddress(owner).endCell();
        const res = await provider.get("getClaimable", [{ type: "slice", cell }]);
        return res.stack.readBigNumber();
    }
    // Time-decayed voting power reader. Matches what QueryVotingPower
    // returns. Off-chain UIs should prefer this over
    // `getLock().votingPower`, which is the historical max.
    async getEffectiveVotingPower(provider, owner) {
        const cell = beginCell().storeAddress(owner).endCell();
        const res = await provider.get("getEffectiveVotingPower", [
            { type: "slice", cell },
        ]);
        return res.stack.readBigNumber();
    }
    // Rewards parked for fold-in on the next PAYLOAD_REWARD that arrives
    // with positive totalVotingPower.
    async getUndistributedRewards(provider) {
        const res = await provider.get("getUndistributedRewards", []);
        return res.stack.readBigNumber();
    }
    // Tracked balance of the bound jetton wallet. Off-chain monitors
    // compare this against the actual wallet balance to detect drift.
    async getWalletBalance(provider) {
        const res = await provider.get("getWalletBalance", []);
        return res.stack.readBigNumber();
    }
}
//# sourceMappingURL=stakingRewards.js.map